import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Checkbox,
  FormControlLabel,
  SelectChangeEvent,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Save as SaveIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { CareerProfile, EXIT_DESCRIPTIONS, EXIT_DESTINATION_GEOS, EXIT_DESTINATION_TEAMS } from '../types';

interface CareerProfileSectionProps {
  selectedAlias?: string;
}

const CareerProfileSection: React.FC<CareerProfileSectionProps> = ({ selectedAlias }) => {
  const [formData, setFormData] = useState<Omit<CareerProfile, 'id' | 'alias' | 'manager_alias'>>({
    tech_lead: false,
    exit_elig_date: null,
    exit_description: null,
    exit_destination_geo: null,
    exit_destination_team: null,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedAlias) {
      fetchCareerProfile();
    }
  }, [selectedAlias]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCareerProfile = async () => {
    if (!selectedAlias) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getCareerProfile(selectedAlias);
      setFormData({
        tech_lead: data.tech_lead,
        exit_elig_date: data.exit_elig_date,
        exit_description: data.exit_description,
        exit_destination_geo: data.exit_destination_geo,
        exit_destination_team: data.exit_destination_team,
      });
    } catch (err: any) {
      if (err.message?.includes('404')) {
        setFormData({
          tech_lead: false,
          exit_elig_date: null,
          exit_description: null,
          exit_destination_geo: null,
          exit_destination_team: null,
        });
      } else {
        setError('Error loading career profile.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = (field: keyof typeof formData) => (event: SelectChangeEvent<string>) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value || null }));
    setSaveSuccess(false);
  };

  const handleDateChange = (field: keyof typeof formData) => (value: Date | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value ? value.toISOString().split('T')[0] : null,
    }));
    setSaveSuccess(false);
  };

  const handleCheckboxChange = (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!selectedAlias) return;
    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      await api.saveCareerProfile(selectedAlias, formData);
      setSaveSuccess(true);
    } catch (err) {
      setError('Error saving career profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!selectedAlias) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h2" gutterBottom>
          Career Profile
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a direct report to view their career profile.
        </Typography>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h2" gutterBottom>
          Career Profile
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading career profile...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h2">Career Profile</Typography>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            size="small"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Career profile saved successfully.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.tech_lead}
                onChange={handleCheckboxChange('tech_lead')}
              />
            }
            label="Tech Lead"
          />

          <DatePicker
            label="Exit Elig Date"
            value={formData.exit_elig_date ? new Date(formData.exit_elig_date + 'T00:00:00') : null}
            onChange={handleDateChange('exit_elig_date')}
            slotProps={{ textField: { fullWidth: true } }}
          />

          <FormControl fullWidth>
            <InputLabel>Exit Description</InputLabel>
            <Select
              value={formData.exit_description || ''}
              label="Exit Description"
              onChange={handleSelectChange('exit_description')}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {EXIT_DESCRIPTIONS.map((val) => (
                <MenuItem key={val} value={val}>{val}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Exit Destination Geo</InputLabel>
            <Select
              value={formData.exit_destination_geo || ''}
              label="Exit Destination Geo"
              onChange={handleSelectChange('exit_destination_geo')}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {EXIT_DESTINATION_GEOS.map((val) => (
                <MenuItem key={val} value={val}>{val}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Exit Destination Team</InputLabel>
            <Select
              value={formData.exit_destination_team || ''}
              label="Exit Destination Team"
              onChange={handleSelectChange('exit_destination_team')}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {EXIT_DESTINATION_TEAMS.map((val) => (
                <MenuItem key={val} value={val}>{val}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>
    </LocalizationProvider>
  );
};

export default CareerProfileSection;
